// Security utilities for input sanitization and validation
import DOMPurify from 'dompurify';

// Admin route prefix - centralized for security
// Admin route prefix - from environment variable for security
export const ADMIN_PREFIX = process.env.REACT_APP_ADMIN_PREFIX || '/admin';

// ============================================
// INPUT SANITIZATION
// ============================================

/**
 * Sanitize HTML content to prevent XSS attacks
 * Removes all potentially dangerous HTML/JS
 */
export const sanitizeHtml = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
};

/**
 * Sanitize plain text - strip all HTML tags
 */
export const sanitizeText = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Sanitize URL to prevent javascript: and data: URLs
 */
export const sanitizeUrl = (url) => {
  if (!url) return '';
  const sanitized = sanitizeText(url).trim();
  // Block dangerous protocols
  if (sanitized.toLowerCase().startsWith('javascript:') ||
      sanitized.toLowerCase().startsWith('data:') ||
      sanitized.toLowerCase().startsWith('vbscript:')) {
    return '';
  }
  return sanitized;
};

/**
 * Escape HTML entities for safe display
 */
export const escapeHtml = (text) => {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

// ============================================
// INPUT VALIDATION
// ============================================

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate URL format
 */
export const isValidUrl = (url) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

/**
 * Validate slug format (alphanumeric, hyphens only)
 */
export const isValidSlug = (slug) => {
  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  return slugRegex.test(slug);
};

/**
 * Validate string length
 */
export const isValidLength = (str, min = 0, max = 10000) => {
  if (!str) return min === 0;
  return str.length >= min && str.length <= max;
};

// ============================================
// FILE VALIDATION
// ============================================

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Validate file for upload
 */
export const validateFile = (file) => {
  const errors = [];

  if (!file) {
    errors.push('No file provided');
    return { valid: false, errors };
  }

  // Check file type by MIME type (not just extension)
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    errors.push('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push('File too large. Maximum size: 5MB');
  }

  // Check for suspicious file names
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    errors.push('Invalid file name');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

// ============================================
// RATE LIMITING (Client-side)
// ============================================

const rateLimitStore = new Map();

/**
 * Check if action is rate limited
 * @param {string} key - Unique identifier for the action
 * @param {number} maxAttempts - Maximum attempts allowed
 * @param {number} windowMs - Time window in milliseconds
 */
export const isRateLimited = (key, maxAttempts = 5, windowMs = 15 * 60 * 1000) => {
  const now = Date.now();
  const attempts = rateLimitStore.get(key) || [];
  
  // Filter attempts within the time window
  const recentAttempts = attempts.filter(time => now - time < windowMs);
  
  if (recentAttempts.length >= maxAttempts) {
    return {
      limited: true,
      remainingTime: Math.ceil((recentAttempts[0] + windowMs - now) / 1000 / 60),
      message: `Too many attempts. Please try again in ${Math.ceil((recentAttempts[0] + windowMs - now) / 1000 / 60)} minutes.`
    };
  }
  
  return { limited: false };
};

/**
 * Record an attempt for rate limiting
 */
export const recordAttempt = (key) => {
  const attempts = rateLimitStore.get(key) || [];
  attempts.push(Date.now());
  rateLimitStore.set(key, attempts);
};

/**
 * Clear rate limit for a key (e.g., on successful login)
 */
export const clearRateLimit = (key) => {
  rateLimitStore.delete(key);
};

// ============================================
// SESSION MANAGEMENT
// ============================================

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
let lastActivityTime = Date.now();
let sessionTimeoutCallback = null;

/**
 * Update last activity time
 */
export const updateActivity = () => {
  lastActivityTime = Date.now();
};

/**
 * Check if session has timed out
 */
export const isSessionTimedOut = () => {
  return Date.now() - lastActivityTime > SESSION_TIMEOUT_MS;
};

/**
 * Start session timeout monitoring
 */
export const startSessionMonitor = (onTimeout) => {
  sessionTimeoutCallback = onTimeout;
  
  // Check every minute
  const checkInterval = setInterval(() => {
    if (isSessionTimedOut() && sessionTimeoutCallback) {
      sessionTimeoutCallback();
      clearInterval(checkInterval);
    }
  }, 60 * 1000);

  // Update activity on user interactions
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });

  return () => {
    clearInterval(checkInterval);
    events.forEach(event => {
      document.removeEventListener(event, updateActivity);
    });
  };
};

// ============================================
// CONTENT SANITIZATION FOR DISPLAY
// ============================================

/**
 * Sanitize content object recursively
 */
export const sanitizeContentObject = (obj) => {
  if (!obj) return obj;
  
  if (typeof obj === 'string') {
    return sanitizeText(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(sanitizeContentObject);
  }
  
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Special handling for URLs
      if (key === 'url' || key === 'href' || key === 'src' || key.endsWith('_url')) {
        sanitized[key] = sanitizeUrl(value);
      } else {
        sanitized[key] = sanitizeContentObject(value);
      }
    }
    return sanitized;
  }
  
  return obj;
};

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Get user-friendly error message (hide technical details)
 */
export const getPublicErrorMessage = (error) => {
  // Log the actual error for debugging (in production, send to logging service)
  console.error('[Error]', error);
  
  // Return generic message to user
  const publicMessages = {
    'Invalid login credentials': 'Invalid email or password',
    'User not found': 'Invalid email or password',
    'Email not confirmed': 'Please verify your email address',
    'Too many requests': 'Too many attempts. Please try again later.',
  };
  
  const errorMessage = error?.message || error;
  return publicMessages[errorMessage] || 'An error occurred. Please try again.';
};
